import React, { useState, useCallback } from 'react';
import './App.css';
import './index.css';
import IslandCanvas from './components/Canvas/IslandCanvas';
import FloatingToolbar from './components/UI/FloatingToolbar';
import ImageUpload from './components/Upload/ImageUpload';
import ImageCropper from './components/Upload/ImageCropper';
import { useCanvas } from './hooks/useCanvas';
import { TOOLS, PAINT_COLORS } from './constants/objectTypes';
import { GRID_CONFIG } from './constants/gridConfig';
import { isValidGridPosition } from './utils/gridUtils';

function App() {
  const {
    currentTool = TOOLS.PAINT,
    setCurrentTool,
    backgroundImage,
    setBackgroundImage,
    objects,
    setObjects,
    addObject,
    removeObject,
    clearCanvas,
    paintData,
    setPaintData,
    selectedColor = PAINT_COLORS.WATER,
    setSelectedColor,
    brushSize,
    setBrushSize,
    eraserSize,
    setEraserSize,
    currentBrushType,
    setCurrentBrushType,
    isLineMode,
    setIsLineMode,
    isEyedropperActive,
    setIsEyedropperActive,
    lineStartPos,
    setLineStartPos,
    isDragging,
    setIsDragging,
    stagePos,
    setStagePos,
    isSpacePressed,
    setIsSpacePressed,
    isShiftPressed,
    setIsShiftPressed,
    lastPaintPos,
    setLastPaintPos,
    lastPointerPos,
    setLastPointerPos,
    isRightPressed,
    setIsRightPressed,
    draggedObject,
    setDraggedObject,
    zoomLevel,
    setZoomLevel,
    step,
    setStep,
    uploadedImage,
    setUploadedImage,
    stageRef
  } = useCanvas();

  const [selectedObjectType, setSelectedObjectType] = useState(null);
  const [brushUpdateTrigger, setBrushUpdateTrigger] = useState(0);

  // 패널 상태 관리
  const [isToolsOpen, setIsToolsOpen] = useState(false);
  const [isObjectsOpen, setIsObjectsOpen] = useState(false);
  const [isEraserOpen, setIsEraserOpen] = useState(false);
  const [isChecklistOpen, setIsChecklistOpen] = useState(false);

  // 모든 패널 닫기
  const closeAllPanels = useCallback(() => {
    setIsToolsOpen(false);
    setIsObjectsOpen(false);
    setIsEraserOpen(false);
    setIsChecklistOpen(false);
  }, []);

  // localStorage에 자동 저장
  const saveToLocalStorage = useCallback(() => {
    try {
      const projectData = {
        objects,
        paintData,
        backgroundImage: backgroundImage ? backgroundImage.src : null,
        selectedColor,
        brushSize,
        currentBrushType,
        currentTool,
        zoomLevel,
        stagePos,
        step,
        timestamp: new Date().toISOString()
      };

      localStorage.setItem('island-designer-autosave', JSON.stringify(projectData));
      console.log('작업 내용이 자동 저장되었습니다.');
    } catch (error) {
      console.error('자동 저장 실패:', error);
    }
  }, [objects, paintData, backgroundImage, selectedColor, brushSize, currentBrushType, currentTool, zoomLevel, stagePos, step]);

  // localStorage에서 불러오기
  const loadFromLocalStorage = useCallback(() => {
    try {
      const savedData = localStorage.getItem('island-designer-autosave');
      if (savedData) {
        const projectData = JSON.parse(savedData);

        // 상태 복원
        if (projectData.objects) setObjects(projectData.objects);
        if (projectData.paintData) setPaintData(projectData.paintData);
        if (projectData.selectedColor) setSelectedColor(projectData.selectedColor);
        if (projectData.brushSize) setBrushSize(projectData.brushSize);
        if (projectData.currentBrushType) setCurrentBrushType(projectData.currentBrushType);
        if (projectData.currentTool) setCurrentTool(projectData.currentTool);
        if (projectData.zoomLevel) setZoomLevel(projectData.zoomLevel);
        if (projectData.stagePos) setStagePos(projectData.stagePos);
        if (projectData.step && projectData.backgroundImage) setStep(projectData.step);
        else setStep('upload');

        // 배경 이미지 복원
        if (projectData.backgroundImage) {
          const img = new Image();
          img.onload = () => {
            setBackgroundImage(img);
            setUploadedImage(img);
          };
          img.src = projectData.backgroundImage;
        }

        console.log('저장된 작업을 불러왔습니다:', projectData.timestamp);
        return true;
      }
    } catch (error) {
      console.error('불러오기 실패:', error);
    }
    return false;
  }, [setObjects, setPaintData, setSelectedColor, setBrushSize, setCurrentBrushType, setCurrentTool, setZoomLevel, setStagePos, setStep, setBackgroundImage, setUploadedImage]);

  // 앱 시작 시 저장된 데이터 로드
  React.useEffect(() => {
    loadFromLocalStorage();
  }, [loadFromLocalStorage]);

  // 자동 저장: 주요 상태가 변경될 때마다 저장
  React.useEffect(() => {
    const timeoutId = setTimeout(() => {
      saveToLocalStorage();
    }, 1000); // 1초 후 저장 (디바운싱)

    return () => clearTimeout(timeoutId);
  }, [objects, paintData, backgroundImage, selectedColor, brushSize, currentBrushType, currentTool, zoomLevel, stagePos, step, saveToLocalStorage]);

  // 브러시 크기 변경 시 커서 업데이트 트리거
  const handleBrushSizeChange = (size) => {
    setBrushSize(size);
    setBrushUpdateTrigger(prev => prev + 1);
  };

  // 브러시 타입 변경 시 커서 업데이트 트리거
  const handleBrushTypeChange = (type) => {
    setCurrentBrushType(type);
    setBrushUpdateTrigger(prev => prev + 1);
  };

  // 스포이드 기능을 위한 색상 추출 함수
  const extractColorFromCanvas = (x, y) => {
    if (!stageRef.current || !backgroundImage) return null;

    const stage = stageRef.current;
    const canvas = stage.toCanvas();
    const ctx = canvas.getContext('2d');

    // 픽셀 데이터 가져오기
    const imageData = ctx.getImageData(x, y, 1, 1);
    const [r, g, b] = imageData.data;

    // RGB를 hex로 변환
    const hex = `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
    return hex;
  };

  // 직선 모드 토글 함수
  const handleLineModeToggle = (isLine) => {
    setIsLineMode(isLine);
    setLineStartPos(null); // 직선 시작점 초기화
  };

  const handleCanvasClick = (gridX, gridY, pixelX, pixelY, isRightClick = false) => {
    console.log('Canvas click - Current tool:', currentTool, 'Selected object:', selectedObjectType);
    if (!isValidGridPosition(gridX, gridY)) return;

    // 스포이드 모드일 때 색상 추출
    if (isEyedropperActive && !isRightClick) {
      const extractedColor = extractColorFromCanvas(pixelX, pixelY);
      if (extractedColor) {
        const customColorObj = {
          id: 'custom',
          name: '추출된 색상',
          color: extractedColor
        };
        setSelectedColor(customColorObj);
        setIsEyedropperActive(false); // 스포이드 모드 종료
      }
      return;
    }

    // 오른쪽 클릭 시 삭제 기능
    if (isRightClick) {
      // 오브젝트 삭제
      const objectToRemove = objects.find(obj => {
        const objSize = obj.size || 1;
        return gridX >= obj.gridX && gridX < obj.gridX + objSize &&
               gridY >= obj.gridY && gridY < obj.gridY + objSize;
      });
      if (objectToRemove) {
        removeObject(objectToRemove.id);
        return;
      }
      
      // 페인트 삭제
      const newPaintData = { ...paintData };
      const key = `${gridX},${gridY}`;
      if (newPaintData[key]) {
        delete newPaintData[key];
        setPaintData(newPaintData);
      }
      return;
    }

    // 오브젝트 도구일 때만 오브젝트 배치 처리
    if (currentTool === TOOLS.OBJECT) {
      console.log('Object tool clicked:', { selectedObjectType, gridX, gridY });
      if (selectedObjectType) {
        const isTree = selectedObjectType.type === 'tree' || selectedObjectType.type === 'fruit_tree' || selectedObjectType.type === 'palm_tree';
        const size = isTree ? 3 : 1;
        
        console.log('Object details:', { type: selectedObjectType.type, size, isTree });
        
        // 배치 가능한 영역인지 확인
        if (gridX + size > GRID_CONFIG.COLS || gridY + size > GRID_CONFIG.ROWS) {
          console.log('Out of bounds');
          return; // 영역을 벗어남
        }
        
        // 충돌 검사
        let hasCollision = false;
        for (let dx = 0; dx < size; dx++) {
          for (let dy = 0; dy < size; dy++) {
            const checkX = gridX + dx;
            const checkY = gridY + dy;
            const existingObject = objects.find(obj => {
              const objSize = obj.size || 1;
              return checkX >= obj.gridX && checkX < obj.gridX + objSize &&
                     checkY >= obj.gridY && checkY < obj.gridY + objSize;
            });
            if (existingObject) {
              hasCollision = true;
              break;
            }
          }
          if (hasCollision) break;
        }
        
        if (!hasCollision) {
          console.log('Adding object:', {
            type: selectedObjectType.type,
            gridX,
            gridY,
            image: selectedObjectType.image,
            name: selectedObjectType.name,
            size
          });
          addObject({
            type: selectedObjectType.type,
            gridX,
            gridY,
            image: selectedObjectType.image,
            name: selectedObjectType.name,
            size
          });
        } else {
          console.log('Collision detected');
        }
      } else {
        console.log('No object type selected');
      }
      return; // 오브젝트 배치 후 리턴
    }
    
    // 다른 도구들 처리
    switch (currentTool) {
      case TOOLS.PAINT:
        console.log('Paint tool triggered:', { gridX, gridY, selectedColor });
        if (selectedColor) {
          // HappyIslandDesigner 방식 페인팅 사용 - paintCells와 동일하게
          const { happyBrush, paintWithHappyBrush } = require('./utils/happyIslandBrush');
          let newPaintData = { ...paintData };

          // 마우스 위치에 따른 삼각형 방향 업데이트
          happyBrush.updateDirection({ x: gridX + 0.5, y: gridY + 0.5 });
          newPaintData = paintWithHappyBrush(newPaintData, gridX, gridY, selectedColor.color, GRID_CONFIG.COLS, GRID_CONFIG.ROWS);

          setPaintData(newPaintData);
          console.log('Paint data updated:', newPaintData);
        }
        break;

      case TOOLS.ERASER:
        console.log('Eraser tool triggered:', { gridX, gridY });

        // 오브젝트 지우기
        const objectToRemove = objects.find(obj => {
          const objSize = obj.size || 1;
          return gridX >= obj.gridX && gridX < obj.gridX + objSize &&
                 gridY >= obj.gridY && gridY < obj.gridY + objSize;
        });
        if (objectToRemove) {
          removeObject(objectToRemove.id);
          console.log('Object removed:', objectToRemove.id);
        }

        // 페인트 데이터 지우기 - 지우개 크기에 따른 영역 지우기
        const { erasePaintArea } = require('./utils/trianglePainting');
        const newPaintData = erasePaintArea(paintData, gridX, gridY, eraserSize, GRID_CONFIG.COLS, GRID_CONFIG.ROWS);
        setPaintData(newPaintData);
        console.log('Paint data erased in area around:', `${gridX},${gridY}`, 'with size:', eraserSize);
        break;

      default:
        break;
    }
  };

  const handleImageUpload = (image) => {
    setUploadedImage(image);
    setStep('crop');
  };
  
  const handleCropComplete = (croppedImage) => {
    const width = window.innerWidth;
    const height = window.innerHeight;
    const scaleX = (width * 0.8) / croppedImage.width;
    const scaleY = (height * 0.8) / croppedImage.height;
    const scale = Math.min(scaleX, scaleY, 1);
    
    setZoomLevel(scale);
    setStagePos({ x: 0, y: 0 }); // CSS로 중앙 정렬
    setBackgroundImage(croppedImage);
    setStep('edit');
  };
  
  const handleCropCancel = () => {
    setUploadedImage(null);
    setStep('upload');
  };

  const handleObjectSelect = (objectType) => {
    setSelectedObjectType(objectType);
    setCurrentTool(TOOLS.OBJECT);
  };
  
  // HappyIslandDesigner 브러시 시스템에 맞춘 커서 생성 함수
  const generateBrushCursor = useCallback(() => {
    if (!backgroundImage) return 'crosshair';

    // HappyIslandDesigner 브러시 시스템에서 실제 브러시 크기와 타입 가져오기
    const { happyBrush } = require('./utils/happyIslandBrush');
    const rawBrushSize = happyBrush.rawBrushSize;
    const brushType = happyBrush.brushType;

    const cellSize = Math.min(backgroundImage.width / 112, backgroundImage.height / 96) * zoomLevel;
    const color = selectedColor ? selectedColor.color.replace('#', '%23') : '%23000000';

    // 크기 0 (삼각형 브러시)
    if (rawBrushSize === 0) {
      const size = Math.max(8, cellSize);
      return `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='${size}' height='${size}' viewBox='0 0 ${size} ${size}'%3E%3Cpath d='M${size/2},0 L${size},${size} L0,${size} Z' fill='${color}' stroke='%23000' stroke-width='1'/%3E%3C/svg%3E") ${size/2} ${size/2}, crosshair`;
    }

    // 크기 1 (1x1 사각형)
    if (rawBrushSize === 1) {
      const size = Math.max(8, cellSize);
      return `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='${size}' height='${size}' viewBox='0 0 ${size} ${size}'%3E%3Crect width='${size}' height='${size}' fill='${color}' stroke='%23000' stroke-width='1'/%3E%3C/svg%3E") ${size/2} ${size/2}, crosshair`;
    }

    // 크기 2
    if (rawBrushSize === 2) {
      const size = Math.max(16, cellSize * 2);

      if (brushType === 'rounded') {
        // 다이아몬드 모양
        const half = size / 2;
        return `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='${size}' height='${size}' viewBox='0 0 ${size} ${size}'%3E%3Cpath d='M${half},0 L${size},${half} L${half},${size} L0,${half} Z' fill='${color}' stroke='%23000' stroke-width='1'/%3E%3C/svg%3E") ${half} ${half}, crosshair`;
      } else {
        // 2x2 사각형
        return `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='${size}' height='${size}' viewBox='0 0 ${size} ${size}'%3E%3Crect width='${size}' height='${size}' fill='${color}' stroke='%23000' stroke-width='1'/%3E%3C/svg%3E") ${size/2} ${size/2}, crosshair`;
      }
    }

    // 크기 3+
    if (rawBrushSize >= 3) {
      const size = Math.max(24, cellSize * rawBrushSize);

      if (brushType === 'rounded') {
        // 팔각형 모양 (간단화해서 원형으로 표시)
        return `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='${size}' height='${size}' viewBox='0 0 ${size} ${size}'%3E%3Ccircle cx='${size/2}' cy='${size/2}' r='${size/2-1}' fill='${color}' stroke='%23000' stroke-width='1'/%3E%3C/svg%3E") ${size/2} ${size/2}, crosshair`;
      } else {
        // 사각형
        return `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='${size}' height='${size}' viewBox='0 0 ${size} ${size}'%3E%3Crect width='${size}' height='${size}' fill='${color}' stroke='%23000' stroke-width='1'/%3E%3C/svg%3E") ${size/2} ${size/2}, crosshair`;
      }
    }

    // 기본값
    return 'crosshair';
  }, [backgroundImage, selectedColor, zoomLevel]);

  // 지우개 커서 생성 함수
  const generateEraserCursor = useCallback(() => {
    if (!backgroundImage) return 'crosshair';

    const cellSize = Math.min(backgroundImage.width / 112, backgroundImage.height / 96) * zoomLevel;
    const size = Math.max(8, cellSize * eraserSize);

    // 지우개는 빨간색 사각형 커서로 표시
    return `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='${size}' height='${size}' viewBox='0 0 ${size} ${size}'%3E%3Crect x='1' y='1' width='${size-2}' height='${size-2}' fill='none' stroke='%23ff4444' stroke-width='2' stroke-dasharray='4,2'/%3E%3Crect x='${size/2-1}' y='${size/2-1}' width='2' height='2' fill='%23ff4444'/%3E%3C/svg%3E") ${size/2} ${size/2}, crosshair`;
  }, [backgroundImage, eraserSize, zoomLevel]);

  // 도구 변경 시 커서 업데이트
  React.useEffect(() => {
    if (stageRef.current) {
      const stage = stageRef.current;

      if (isSpacePressed) {
        stage.container().style.cursor = 'grab';
      } else if (isRightPressed) {
        stage.container().style.cursor = 'crosshair';
      } else if (isEyedropperActive) {
        stage.container().style.cursor = 'crosshair';
      } else {
        switch (currentTool) {
          case TOOLS.PAINT:
            stage.container().style.cursor = generateBrushCursor();
            break;
          case TOOLS.ERASER:
            stage.container().style.cursor = generateEraserCursor();
            break;
          case TOOLS.OBJECT:
            stage.container().style.cursor = 'pointer';
            break;
          default:
            stage.container().style.cursor = generateBrushCursor();
        }
      }
    }
  }, [currentTool, brushSize, eraserSize, selectedColor, currentBrushType, isSpacePressed, isRightPressed, isEyedropperActive, zoomLevel, backgroundImage, brushUpdateTrigger, generateBrushCursor, generateEraserCursor, stageRef]);

  // 저장된 데이터 삭제
  const clearSavedData = () => {
    if (window.confirm('저장된 모든 데이터를 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다.')) {
      try {
        localStorage.removeItem('island-designer-autosave');
        console.log('저장된 데이터가 삭제되었습니다.');
        // 페이지 새로고침으로 초기 상태로 돌아감
        window.location.reload();
      } catch (error) {
        console.error('데이터 삭제 실패:', error);
      }
    }
  };

  // 파일로 저장 - 페인팅이 적용된 이미지 PNG로 내보내기
  const handleSaveProject = () => {
    if (!stageRef.current) {
      console.error('Canvas를 찾을 수 없습니다.');
      return;
    }

    try {
      // Konva Stage를 이미지로 변환
      const uri = stageRef.current.toDataURL();

      // 다운로드 링크 생성
      const link = document.createElement('a');
      link.href = uri;
      link.download = `island-design-${new Date().getTime()}.png`;
      link.click();
    } catch (error) {
      console.error('이미지 저장 실패:', error);
      alert('이미지 저장 중 오류가 발생했습니다.');
    }
  };

  // 디바이스 감지
  const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  const isTablet = isTouchDevice && (window.innerWidth >= 768 && window.innerWidth <= 1024);

  return (
    <div style={{
      width: '100vw',
      height: '100vh',
      backgroundColor: '#7AD8C6',
      overflow: 'hidden', // 아이패드에서 스크롤 방지
      touchAction: isTablet ? 'none' : 'auto', // 터치 제스처 제어
      WebkitTouchCallout: 'none', // iOS 길게 누르기 메뉴 비활성화
      WebkitUserSelect: 'none', // 텍스트 선택 방지
      userSelect: 'none'
    }}>
      <FloatingToolbar
        step={step}
        currentTool={currentTool}
        onToolChange={setCurrentTool}
        selectedColor={selectedColor}
        onColorSelect={setSelectedColor}
        brushSize={brushSize}
        setBrushSize={handleBrushSizeChange}
        eraserSize={eraserSize}
        setEraserSize={setEraserSize}
        currentBrushType={currentBrushType}
        setCurrentBrushType={handleBrushTypeChange}
        isLineMode={isLineMode}
        onLineModeToggle={handleLineModeToggle}
        selectedObjectType={selectedObjectType}
        onObjectSelect={handleObjectSelect}
        onImageUpload={handleImageUpload}
        backgroundImage={backgroundImage}
        setBackgroundImage={setBackgroundImage}
        setUploadedImage={setUploadedImage}
        setStep={setStep}
        onSaveProject={handleSaveProject}
        onClearCanvas={clearCanvas}
        onClearSavedData={clearSavedData}
        isToolsOpen={isToolsOpen}
        setIsToolsOpen={setIsToolsOpen}
        isObjectsOpen={isObjectsOpen}
        setIsObjectsOpen={setIsObjectsOpen}
        isEraserOpen={isEraserOpen}
        setIsEraserOpen={setIsEraserOpen}
        isChecklistOpen={isChecklistOpen}
        setIsChecklistOpen={setIsChecklistOpen}
      />
      
      {step === 'upload' && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%'
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '15px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
            padding: '20px'
          }}>
            <ImageUpload onImageUpload={handleImageUpload} />
          </div>
        </div>
      )}
      
      {step === 'edit' && (
        <IslandCanvas
          backgroundImage={backgroundImage}
          objects={objects}
          onCanvasClick={handleCanvasClick}
          stageRef={stageRef}
          currentTool={currentTool}
          onToolChange={setCurrentTool}
          paintData={paintData}
          setPaintData={setPaintData}
          selectedColor={selectedColor}
          brushSize={brushSize}
          eraserSize={eraserSize}
          currentBrushType={currentBrushType}
          isLineMode={isLineMode}
          isEyedropperActive={isEyedropperActive}
          lineStartPos={lineStartPos}
          setLineStartPos={setLineStartPos}
          isDragging={isDragging}
          setIsDragging={setIsDragging}
          stagePos={stagePos}
          setStagePos={setStagePos}
          isSpacePressed={isSpacePressed}
          setIsSpacePressed={setIsSpacePressed}
          isShiftPressed={isShiftPressed}
          setIsShiftPressed={setIsShiftPressed}
          lastPaintPos={lastPaintPos}
          setLastPaintPos={setLastPaintPos}
          lastPointerPos={lastPointerPos}
          setLastPointerPos={setLastPointerPos}
          isRightPressed={isRightPressed}
          setIsRightPressed={setIsRightPressed}
          draggedObject={draggedObject}
          setDraggedObject={setDraggedObject}
          selectedObjectType={selectedObjectType}
          zoomLevel={zoomLevel}
          setZoomLevel={setZoomLevel}
          removeObject={removeObject}
          onClosePanel={closeAllPanels}
        />
      )}
      
      {step === 'crop' && uploadedImage && (
        <ImageCropper
          image={uploadedImage}
          onCropComplete={handleCropComplete}
          onCancel={handleCropCancel}
        />
      )}
    </div>
  );
}

export default App;